import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { links, hrLinks } from "./sidebarLink";
import { IS_HR_SITE, DEFAULT_ROUTE } from "../../config/access";
import logoFull from "../assets/logo-full.png";
import logoMark from "../assets/logo-mark.png";
import {
  useGetMeQuery,
  useLogoutAdminMutation,
  useResetAdminPasswordMutation,
} from "../../services/auth";
import ResetPassword from "../common/reset-password/ResetPassword";

const EXPANDED_WIDTH = 260;
const COLLAPSED_WIDTH = 84;

function Sidebar({ isSidebarOpen, closeSidebar, isCollapsed, setIsCollapsed }) {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: user } = useGetMeQuery(null, {
    skip: !localStorage.getItem("token"),
  });
  const [resetAdminPassword, { isError: resetError }] =
    useResetAdminPasswordMutation();
  const [logoutAdmin] = useLogoutAdminMutation();

  const collapsed = !isMobile && isCollapsed;
  const navLinks = IS_HR_SITE ? hrLinks : links;

  const fullName = [user?.user?.first_name, user?.user?.last_name]
    .filter(Boolean)
    .join(" ");
  const role = user?.user?.role
    ? user.user.role.charAt(0).toUpperCase() + user.user.role.slice(1).toLowerCase()
    : "";
  const initials =
    [user?.user?.first_name?.[0], user?.user?.last_name?.[0]]
      .filter(Boolean)
      .join("")
      .toUpperCase() || "A";

  const handleAccountMenuOpen = (event) => {
    if (collapsed) {
      setIsCollapsed(false);
      return;
    }
    setAccountMenuAnchor(event.currentTarget);
  };
  const closeAccountMenu = () => setAccountMenuAnchor(null);

  const handleLogout = async () => {
    try {
      const adminId = user?.user?.id;
      await logoutAdmin({ adminId }).unwrap();
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("role");
      window.location.href = "/";
    } catch (error) {
      console.error("Logout error:", error);
      alert("Logout failed");
    }
  };

  const handleResetPasswordSubmit = async (newPassword, oldPassword) => {
    setIsSubmitting(true);
    try {
      const adminId = user?.user?.id;
      await resetAdminPassword({
        adminId,
        newPassword,
        oldPassword,
      }).unwrap();
      setShowResetModal(false);
      handleLogout();
    } catch (error) {
      if (error.status === 401) {
        alert("Old password is incorrect");
      } else {
        console.error("Reset password error:", error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleDropdown = (index) => {
    if (collapsed) {
      // A collapsed rail has no room for a submenu — expand the sidebar first.
      setIsCollapsed(false);
      setOpenDropdowns((prev) => ({ ...prev, [index]: true }));
      return;
    }
    setOpenDropdowns((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const navContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          minHeight: 84,
          px: collapsed ? 1 : 3,
        }}
      >
        <Link
          to={DEFAULT_ROUTE}
          style={{ display: "flex", alignItems: "center" }}
        >
          <img
            src={collapsed ? logoMark : logoFull}
            alt="Feelix"
            style={{
              height: collapsed ? 36 : 44,
              width: "auto",
              display: "block",
            }}
          />
        </Link>
      </Box>

      <List sx={{ flex: 1, px: collapsed ? 1 : 1.5, overflowY: "auto", overflowX: "hidden" }}>
        {navLinks.map(({ title, path, icon: Icon, type, children }, index) => {
          if (type === "button") {
            const isActive = location.pathname.startsWith(path);
            return (
              <Tooltip
                key={title}
                title={title}
                placement="right"
                disableHoverListener={!collapsed}
              >
                <ListItemButton
                  component={Link}
                  to={path}
                  onClick={closeSidebar}
                  selected={isActive}
                  sx={navItemSx(theme, collapsed)}
                >
                  <ListItemIcon sx={navIconSx(theme, collapsed, isActive)}>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={title}
                      sx={{ minWidth: 0, overflow: "hidden" }}
                      slotProps={{
                        primary: { fontSize: 14, fontWeight: isActive ? 600 : 500, noWrap: true },
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          }

          if (type === "dropdown") {
            const isOpen = !collapsed && !!openDropdowns[index];
            const isParentActive = children?.some((child) =>
              location.pathname.includes(child.path)
            );
            return (
              <Box key={title}>
                <Tooltip
                  title={title}
                  placement="right"
                  disableHoverListener={!collapsed}
                >
                  <ListItemButton
                    onClick={() => toggleDropdown(index)}
                    selected={isParentActive && !isOpen}
                    sx={navItemSx(theme, collapsed)}
                  >
                    <ListItemIcon sx={navIconSx(theme, collapsed, isParentActive)}>
                      <Icon fontSize="small" />
                    </ListItemIcon>
                    {!collapsed && (
                      <>
                        <ListItemText
                          primary={title}
                          sx={{ minWidth: 0, overflow: "hidden" }}
                          slotProps={{
                            primary: {
                              fontSize: 14,
                              fontWeight: isParentActive ? 600 : 500,
                              noWrap: true,
                            },
                          }}
                        />
                        <ExpandMoreIcon
                          fontSize="small"
                          sx={{
                            flexShrink: 0,
                            ml: 0.5,
                            color: "text.secondary",
                            transition: "transform 0.2s ease-in-out",
                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                          }}
                        />
                      </>
                    )}
                  </ListItemButton>
                </Tooltip>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 5.5, pr: 1 }}>
                    {children.map((child) => {
                      const isChildActive = location.pathname.includes(child.path);
                      return (
                        <ListItemButton
                          key={child.title}
                          component={Link}
                          to={child.path}
                          onClick={closeSidebar}
                          selected={isChildActive}
                          sx={{
                            borderRadius: 2,
                            mb: 0.5,
                            py: 0.75,
                            "&.Mui-selected": {
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              "&:hover": {
                                bgcolor: alpha(theme.palette.primary.main, 0.15),
                              },
                            },
                          }}
                        >
                          <ListItemText
                            primary={child.title}
                            sx={{ minWidth: 0, overflow: "hidden" }}
                            slotProps={{
                              primary: {
                                fontSize: 13,
                                fontWeight: isChildActive ? 600 : 400,
                                noWrap: true,
                              },
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          }

          return null;
        })}
      </List>

      <Divider sx={{ mx: collapsed ? 1 : 1.5 }} />

      <Box sx={{ px: collapsed ? 1 : 1.5, py: 1.5 }}>
        <Tooltip
          title={collapsed ? `${fullName}${role ? ` · ${role}` : ""}` : ""}
          placement="right"
          disableHoverListener={!collapsed}
        >
          <Box
            onClick={handleAccountMenuOpen}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              cursor: "pointer",
              borderRadius: 2,
              p: 1,
              justifyContent: collapsed ? "center" : "flex-start",
              "&:hover": { bgcolor: theme.palette.action.hover },
            }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                fontSize: 13,
                fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: theme.palette.primary.main,
              }}
            >
              {initials}
            </Avatar>
            {!collapsed && (
              <>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    noWrap
                    sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}
                  >
                    {fullName || "Admin"}
                  </Typography>
                  <Typography
                    noWrap
                    sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.3 }}
                  >
                    {role}
                  </Typography>
                </Box>
                <MoreVertIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </>
            )}
          </Box>
        </Tooltip>

        <Menu
          anchorEl={accountMenuAnchor}
          open={!!accountMenuAnchor}
          onClose={closeAccountMenu}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <MenuItem
            onClick={() => {
              closeAccountMenu();
              setShowResetModal(true);
            }}
          >
            <ListItemIcon>
              <LockResetOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Reset Password
          </MenuItem>
          <MenuItem
            onClick={() => {
              closeAccountMenu();
              handleLogout();
            }}
          >
            <ListItemIcon>
              <LogoutOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Box>

      {!isMobile && (
        <Box sx={{ display: "flex", justifyContent: "center", pb: 1.5 }}>
          <IconButton
            onClick={() => setIsCollapsed((prev) => !prev)}
            size="small"
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              color: "text.secondary",
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
            }}
          >
            {collapsed ? (
              <ChevronRightIcon fontSize="small" />
            ) : (
              <ChevronLeftIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      )}
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? isSidebarOpen : true}
      onClose={closeSidebar}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        flexShrink: 0,
        whiteSpace: "nowrap",
        "& .MuiDrawer-paper": {
          width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
          boxSizing: "border-box",
          border: "none",
          borderRight: `1px solid ${theme.palette.divider}`,
          bgcolor: "#fff",
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: "hidden",
        },
      }}
    >
      {navContent}
      <ResetPassword
        show={showResetModal}
        close={setShowResetModal}
        onHide={() => setShowResetModal(false)}
        onSubmit={handleResetPasswordSubmit}
        isSubmitting={isSubmitting}
        FormError={resetError}
      />
    </Drawer>
  );
}

const navItemSx = (theme, collapsed) => ({
  minHeight: 46,
  borderRadius: 2,
  mb: 0.5,
  justifyContent: collapsed ? "center" : "flex-start",
  px: collapsed ? 1.5 : 1.5,
  "&.Mui-selected": {
    bgcolor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    "&:hover": {
      bgcolor: alpha(theme.palette.primary.main, 0.15),
    },
  },
  "&:hover": {
    bgcolor: theme.palette.action.hover,
  },
});

const navIconSx = (theme, collapsed, active) => ({
  minWidth: 0,
  mr: collapsed ? 0 : 2,
  justifyContent: "center",
  color: active ? theme.palette.primary.main : "text.secondary",
});

export default Sidebar;
